import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useOrgCurrency } from '../../../hooks/useOrgCurrency.js';
import { Plus, FileText, Edit, Trash2, Eye } from 'lucide-react';
import api from '../../../api.js';
import ContractForm from '../forms/ContractForm.jsx';
import ContractDetailModal from '../modals/ContractDetailModal.jsx';

// Les libelles d'action workflow sont resolus par t('insurance.workflowActions.<code>')
// et les statuts par t('insurance.contractStatus.<code>').

function ContractsTab({ onMessage, readOnly }) {
  const { t } = useTranslation();
  // Devise de l'organisation de la ligne - un seul outil monetaire.
  const { money } = useOrgCurrency();
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [selectedContract, setSelectedContract] = useState(null);

  const loadContracts = async () => {
    try {
      setLoading(true);
      const response = await api.insurance_contract.getAll();
      setContracts(response.data || []);
    } catch (error) {
      console.error('Error loading contracts:', error);
      onMessage?.({
        title: t('insurance.messages.errorTitle'),
        message: t('insurance.contractsTab.loadFailed'),
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadContracts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleWorkflowAction = async (contractId, action) => {
    try {
      const workflowActions = {
        suspend: api.insurance_contract.workflow.suspend,
        reactivate: api.insurance_contract.workflow.reactivate,
        terminate: api.insurance_contract.workflow.terminate,
      };
      const workflowFn = workflowActions[action];
      if (workflowFn) {
        const res = await workflowFn(contractId);
        if (res.ok) {
          onMessage?.({
            title: t('insurance.messages.successTitle'),
            message: t('insurance.contractsTab.actionDone', {
              action: t(`insurance.workflowActions.${action}`, { defaultValue: action }),
            }),
            type: 'success',
          });
          setSelectedContract(null);
          loadContracts();
        }
      }
    } catch (error) {
      onMessage?.({
        title: t('insurance.messages.errorTitle'),
        message: t('insurance.contractsTab.actionFailed', { action }),
        type: 'error',
      });
    }
  };

  const handleDelete = async id => {
    if (!window.confirm(t('insurance.contractsTab.confirmDelete'))) return;
    try {
      await api.insurance_contract.delete(id);
      onMessage?.({ title: t('insurance.messages.successTitle'), message: t('insurance.contractsTab.deleted'), type: 'success' });
      loadContracts();
    } catch (error) {
      onMessage?.({
        title: t('insurance.messages.errorTitle'),
        message: t('insurance.contractsTab.deleteFailed'),
        type: 'error',
      });
    }
  };

  const getStatusColor = status => {
    const colors = {
      draft: 'bg-gray-100 text-gray-800',
      active: 'bg-green-100 text-green-800',
      suspended: 'bg-orange-100 text-orange-800',
      terminated: 'bg-red-100 text-red-800',
      expired: 'bg-gray-100 text-gray-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getWorkflowActions = status => {
    const actions = {
      draft: [],
      active: ['suspend', 'terminate'],
      suspended: ['reactivate'],
    };
    return actions[status] || [];
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-600">{t('common.states.loading')}</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {showForm && (
        <ContractForm
          contract={editingItem}
          onClose={() => {
            setShowForm(false);
            setEditingItem(null);
          }}
          onSuccess={() => {
            loadContracts();
            setShowForm(false);
            setEditingItem(null);
          }}
          onMessage={onMessage}
        />
      )}

      {selectedContract && (
        <ContractDetailModal
          contract={selectedContract}
          onClose={() => setSelectedContract(null)}
          onUpdate={loadContracts}
          onMessage={onMessage}
          onWorkflowAction={handleWorkflowAction}
        />
      )}

      <div className="flex justify-between items-center">
        <p className="text-gray-600">{t('insurance.contractsTab.total', { count: contracts.length })}</p>
        {!readOnly && (
          <button
            onClick={() => {
              setEditingItem(null);
              setShowForm(true);
            }}
            className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            {t('insurance.actions.newContract')}
          </button>
        )}
      </div>

      {contracts.length === 0 ? (
        <div className="bg-white p-12 rounded-lg shadow-md text-center">
          <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-900 mb-2">{t('insurance.contractsTab.emptyTitle')}</h3>
          <p className="text-gray-600">{t('insurance.contractsTab.emptyHint')}</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  {t('insurance.contractsTab.col.contract')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  {t('insurance.contractsTab.col.insurer')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  {t('insurance.contractsTab.col.type')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  {t('insurance.contractsTab.col.period')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  {t('insurance.contractsTab.col.premium')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  {t('insurance.contractsTab.col.status')}
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  {t('insurance.contractsTab.col.actions')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {contracts.map(contract => (
                <tr key={contract.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-900">
                      {contract.policy_number}
                    </div>
                    <div className="text-sm text-gray-500">{contract.product_name}</div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">{contract.insurer_name}</td>
                  <td className="px-6 py-4 text-sm text-gray-900">{contract.contract_type}</td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">{contract.effective_date}</div>
                    <div className="text-sm text-gray-500">{t('insurance.contractsTab.dateTo', { date: contract.expiry_date })}</div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {money(contract.total_premium_ttc, contract)}
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className={`px-2 py-1 text-xs rounded-full ${getStatusColor(contract.status)}`}
                    >
                      {t(`insurance.contractStatus.${contract.status}`, { defaultValue: contract.status })}
                    </span>
                    <div className="mt-2 flex gap-1">
                      {getWorkflowActions(contract.status).map(action => (
                        <button
                          key={action}
                          onClick={() => handleWorkflowAction(contract.id, action)}
                          className={`text-xs px-2 py-1 rounded ${
                            action === 'terminate' || action === 'suspend'
                              ? 'bg-red-500 text-white hover:bg-red-600'
                              : 'bg-blue-500 text-white hover:bg-blue-600'
                          }`}
                        >
                          {t(`insurance.workflowActions.${action}`, { defaultValue: action })}
                        </button>
                      ))}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => setSelectedContract(contract)}
                        className="flex items-center gap-1 px-2 py-1 text-green-600 hover:bg-green-50 rounded text-sm"
                        title={t('insurance.contractsTab.viewDetail')}
                      >
                        <Eye className="w-4 h-4" />
                        <span className="hidden lg:inline">{t('insurance.contractsTab.view')}</span>
                      </button>
                      {!readOnly && (
                        <>
                          <button
                            onClick={() => {
                              setEditingItem(contract);
                              setShowForm(true);
                            }}
                            className="flex items-center gap-1 px-2 py-1 text-blue-600 hover:bg-blue-50 rounded text-sm"
                            title={t('insurance.contractsTab.editContract')}
                          >
                            <Edit className="w-4 h-4" />
                            <span className="hidden lg:inline">{t('common.actions.edit')}</span>
                          </button>
                          <button
                            onClick={() => handleDelete(contract.id)}
                            className="flex items-center gap-1 px-2 py-1 text-red-600 hover:bg-red-50 rounded text-sm"
                            title={t('insurance.contractsTab.deleteContract')}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default ContractsTab;
